import { Injectable } from '@angular/core';
import { CvfState } from 'src/app/core/data-models/survey-modes';

export type CvfAllocationState = 'now' | 'future';

/**
 * The persisted CVF answer shape. Both maps are always present regardless of
 * survey mode -- two-batch fills `now` in the first pass and `future` in the
 * second, so the stored shape never varies and backend scoring is unaffected.
 */
export interface CvfAnswer {
  now: Record<string, number>;
  future: Record<string, number>;
}

/**
 * Reads and validates constant-sum CVF answers. Shared by the question
 * renderer, which shows the running totals, and the culture taker, which gates
 * navigation on them -- so the 100-point rule lives in exactly one place.
 */
@Injectable({ providedIn: 'root' })
export class CvfAnswerService {

  /** Each CVF state column must sum to exactly this many points. */
  readonly total = 100;

  /** True when the answer is a usable CVF object rather than null/array/scalar. */
  isCvfAnswer(answer: any): boolean {
    return !!answer && typeof answer === 'object' && !Array.isArray(answer);
  }

  stateTotal(answer: any, state: CvfAllocationState): number {
    if (!this.isCvfAnswer(answer) || !answer[state]) return 0;
    return Object.values(answer[state]).reduce((a: number, b: any) => a + Number(b || 0), 0) as number;
  }

  /** Which allocation columns a step renders for the given CVF display state. */
  statesFor(state: CvfState): CvfAllocationState[] {
    if (state === 'now') return ['now'];
    if (state === 'future') return ['future'];
    return ['now', 'future'];
  }

  /**
   * The states that do not yet total `total`. Empty means the answer is valid
   * for the states asked about, so callers can gate navigation on `.length`.
   */
  invalidStates(answer: any, states: CvfAllocationState[]): CvfAllocationState[] {
    if (!this.isCvfAnswer(answer)) return [...states];
    return states.filter(state => this.stateTotal(answer, state) !== this.total);
  }

  /** Points left to allocate in this state before the total hits `total`. */
  remainingBudget(answer: any, state: CvfAllocationState): number {
    return Math.max(0, this.total - this.stateTotal(answer, state));
  }

  /** Clamps a proposed cell value to [0, total] and to whatever the other cells leave in the budget. */
  clampCellValue(answer: any, state: CvfAllocationState, optionKey: string, value: number): number {
    if (!this.isCvfAnswer(answer) || !answer[state]) return Math.max(0, value);
    const otherTotal = this.stateTotal(answer, state) - (answer[state][optionKey] || 0);
    return Math.min(Math.max(0, this.total - otherTotal), Math.max(0, value));
  }

  /** Seeds both maps so the persisted shape never varies by survey mode. */
  ensureShape(question: any): void {
    if (!question) return;

    if (!this.isCvfAnswer(question.answer)) {
      question.answer = { now: {}, future: {} };
    }
    if (!question.answer.now) question.answer.now = {};
    if (!question.answer.future) question.answer.future = {};

    question.questionOptions?.forEach((option: any) => {
      if (question.answer.now[option.key] === undefined) question.answer.now[option.key] = 0;
      if (question.answer.future[option.key] === undefined) question.answer.future[option.key] = 0;
    });
  }
}
